import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Form, Skeleton } from 'antd'
import { VStack, HStack } from '../../../../../ui/Stack'
import { useSelector } from 'react-redux'
import {
  getHrFormData,
  getHrFormError,
  getHrFormLoading,
  getVisibleFormData,
} from '../../selectors'
import { DefaultJsonForm } from '../../../../../components/v2/default-json-form'
import { useDebounce } from '../../../../../hooks/useDebounce/useDebounce'
import { useHrSaveResultForms } from '../../hooks/useHrSaveResultForms'

interface HrFormContentProps {
  className?: string
  id: string | number
}

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
}

export const HrFormContent: React.FC<HrFormContentProps> = memo((props: HrFormContentProps) => {
  const { id } = props
  const [form] = Form.useForm()
  const hrFormData = useSelector(getHrFormData)
  const hrFormIsLoading = useSelector(getHrFormLoading)
  const hrFormError = useSelector(getHrFormError)
  const visibleFormData = useSelector(getVisibleFormData)
  const { saveResult } = useHrSaveResultForms()
  const formRef = useRef<typeof visibleFormData>()
  const [localFormData, setLocalFormData] = useState<any>(null)

  useEffect(() => {
    if (visibleFormData) {
      formRef.current = visibleFormData
      if (visibleFormData.combinedFormData) {
        setLocalFormData(visibleFormData.combinedFormData)
      }
    }
  }, [visibleFormData])

  const saveCase = useCallback(
    async (isNextStage = false) => {
      await saveResult(
        id,
        formRef.current,
        hrFormData?.stages?.current,
        false,
        () => {},
        isNextStage,
      )
    },
    [id, saveResult, hrFormData],
  )

  const debounceSaveCase = useDebounce(saveCase, 1000)

  const onFormDataChange = useCallback(
    async ({ formData }) => {
      if (!formRef.current) {
        return
      }
      setLocalFormData(formData)

      const candidateFields = formRef.current.candidateFields || []
      const requestFields = formRef.current.requestFields || []

      const candidateData = {}
      const requestData = {}

      Object.keys(formData).forEach(key => {
        if (candidateFields.includes(key)) {
          candidateData[key] = formData[key]
        }
        if (requestFields.includes(key)) {
          requestData[key] = formData[key]
        }
      })

      formRef.current.candidate = { ...candidateData }
      formRef.current.request = { ...requestData }
      debounceSaveCase()
    },
    [debounceSaveCase],
  )

  if (hrFormError && !hrFormIsLoading && !hrFormData) {
    return (
      <HStack gap={'8'} className={'mapWrapper'}>
        При выполнении запроса возникла ошибка. Попробуйте перезагрузить страницу или открыть
        карточку повторно.
      </HStack>
    )
  }

  return (
    <>
      <HStack gap={'8'} max>
        <Form
          {...formItemLayout}
          layout="horizontal"
          form={form}
          scrollToFirstError={true}
          style={{ width: '100%' }}
        >
          {hrFormError && !hrFormIsLoading ? (
            <div className={'alertWrapper'}>
              <Alert
                message="При выполнении операции возникла ошибка:"
                showIcon
                type="error"
                description={hrFormError}
              />
            </div>
          ) : null}
          {!hrFormIsLoading ? (
            <VStack gap={'16'} max align={'stretch'} className={'hrForm'}>
              {visibleFormData && localFormData && (
                <DefaultJsonForm
                  formData={localFormData}
                  schema={visibleFormData?.combinedSchema}
                  onChange={onFormDataChange}
                  orientation="horizontal"
                />
              )}
            </VStack>
          ) : (
            <Skeleton active />
          )}
        </Form>
      </HStack>
    </>
  )
})

HrFormContent.displayName = 'HrFormContent'
